import { useCallback, useMemo, useRef, useState } from 'react';

import { useAnswerSaves } from '../../contexts/answer-saves';
import { useLanguage } from '../../contexts/language';
import { useVisit } from '../../hooks/visit';

import { ChoiceList } from '../choice-list';
import { StepActions } from '../step-actions';

import styles from './style.module.css';

// One item of a questionnaire with the instrument's instructions above it,
// both in the questionnaire's own language and wording, which is why that
// part of the screen carries its lang. The answer is saved as soon as it is
// chosen, through the answer saves above every screen, which keep an item's
// writes in order (#114). Next waits until the latest choice is saved; after
// a failed save it tries that choice again first. It moves on even without
// an answer, because the check before submitting is where every item must be
// answered.
export function TestItem({ attemptId, questionnaire, position, chosen, onBack, onNext }) {
  const { text, translate } = useLanguage();
  const { entryOf, choose, settle } = useAnswerSaves();
  const capture = useVisit();
  const [ retrying, setRetrying ] = useState(false);
  const first = useRef(null);

  const entry = entryOf({ attemptId, position, stored: chosen });
  const item = questionnaire.items[ position - 1 ];
  const saving = entry.sending === true || (entry.failure == null && entry.latest !== entry.stored);

  // Each option carries its number, as on the paper form.
  const options = useMemo(function() {
    return questionnaire.options.map(function({ value: option, label }) {
      return { key: String(option), label: `${ option } · ${ label }` };
    });
  }, [ questionnaire ]);

  const onChange = useCallback(function(next) {
    choose(entry, next);
  }, [ entry, choose ]);

  const onSubmit = useCallback(function(event) {
    event.preventDefault();

    if (entry.failure == null) {
      onNext();

      return null;
    }

    setRetrying(true);

    // The answer is tried again whatever happens; moving on or focusing the
    // choices belongs to this item's visit (#135).
    const owns = capture();

    return settle(entry).then(function(saved) {
      if (owns() === false) {
        return;
      }

      setRetrying(false);

      if (saved === true) {
        onNext();

        return;
      }

      first.current.focus();
    });
  }, [ entry, settle, onNext, capture ]);

  return (
    <form className={ styles.form } onSubmit={ onSubmit } noValidate>
      <div className={ styles.card } lang={ questionnaire.language }>
        <p className={ styles.instructions }>{ questionnaire.instructions }</p>

        <ChoiceList
          ref={ first }
          name='answer'
          legend={ item.text }
          options={ options }
          value={ entry.latest }
          onChange={ onChange }
          error={ translate(entry.failure) }
        />
      </div>

      <StepActions backLabel={ text.previousQuestion } onBack={ onBack } submitLabel={ text.next } busy={ saving || retrying }/>
    </form>
  );
}
